import { IonCol, IonRow, useIonAlert } from '@ionic/react';
import { unwrapResult } from '@reduxjs/toolkit';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useHistory } from 'react-router';

import { getRepairOrderByRepairOrderNumber } from '../../api/dms/repairOrder.api';
import { logTransaction } from '../../common/log';
import { getClockingId, getJobId, getStateDateAsString } from '../../common/util';
import { ADHOC_JOB_CODE } from '../../models/dms/adhocJob.model';
import { ClockingDirection } from '../../models/dms/clocking.model';
import { IRepairOrder, mapRepairOrderDetailToRepairOrderWithJobs } from '../../models/dms/repairOrder.model';
import { IRepairOrderJob } from '../../models/dms/repairOrderJob.model';
import { UNKNOWN_TECH } from '../../models/dms/technician.model';
import { IThunkError } from '../../models/error.model';
import { LogType } from '../../models/log.model';
import { RouteNames } from '../../models/routes.model';
import { useClockOffLabourModal } from '../../providers/ClockOffLabourModalProvider';
import { useDataSync } from '../../providers/DataSyncProvider';
import { useToast } from '../../providers/ToastProvider';
import store from '../../store';
import { setIsInActionState } from '../../store/appSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setRepairOrderHasSyncStoreAsync, updateJobsInRepairOrderSyncStoreAsync } from '../../store/repairOrderSyncSlice';
import { clockOnLabourClockingAsync, resumeLabourClockingAsync } from '../../store/technicianClockingSlice';
import { DashboardSelectedTab } from '../dashboardFooter/DashboardFooter';
import JobCard from './jobCard/JobCard';
import JobCardSkeleton from './jobCard/JobCardSkeleton';
import { IonGridWrapper } from './JobCardList.styles';

interface IComponentProps {
  repairOrder: IRepairOrder | undefined;
}

interface RefHandle {
  loadJobsManually: () => void;
}

const JobCardList: React.ForwardRefRenderFunction<RefHandle, IComponentProps> = function ({ repairOrder }, forwardedRef) {
  useImperativeHandle(forwardedRef, () => ({
    loadJobsManually,
  }));

  const [jobs, setJobs] = useState([] as IRepairOrderJob[]);
  const [selectedJobTypeCodeRo, setSelectedJobTypeCodeRo] = useState('');
  const [loadingJobsManually, setLoadingJobsManually] = useState(false);
  const [keyId, setKeyId] = useState(new Date().getTime());
  const {
    actions: { errorToast },
  } = useToast();

  const [presentAlert] = useIonAlert();
  const history = useHistory();

  const { repairOrderSync, repairOrderSyncLoading } = useAppSelector((state) => state.repairOrderSync);
  const { labourClocking } = useAppSelector((state) => state.technicianClocking);
  const { isDemo } = useAppSelector((state) => state.app);
  const {
    state: { requestQueueSyncTimer, repairOrderSyncTimer },
  } = useDataSync();
  const {
    actions: { showClockOffLabourModal },
  } = useClockOffLabourModal();

  const dispatch = useAppDispatch();

  useEffect(() => {
    setSelectedJobTypeCodeRo(getClockingId(labourClocking));
    setKeyId(new Date().getTime());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labourClocking]);

  useEffect(() => {
    if (repairOrder?.repairOrderNumber || repairOrder?.isAdhoc) {
      setRepairOrderJobs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repairOrder]);

  const handleClickJob = async (job: IRepairOrderJob) => {
    if (!job.isInvoiced) {
      if (repairOrder) {
        let roCopy = { ...repairOrder } as IRepairOrder;
        if (repairOrder) {
          const { labourClocking } = store.getState().technicianClocking;
          const { selectedRepairOrder } = store.getState().repairOrder;
          const { technician } = store.getState().auth;

          // If currently clocked on labour, show the clock-on modal
          // Otherwise, simplify the process by showing a clock-on alert
          const matchingJob = getJobId(job) === selectedJobTypeCodeRo;

          // Clicked on real RO job?
          if (job.jobCode !== ADHOC_JOB_CODE) {
            logTransaction(LogType.Info, `User created adhoc job for RO ${roCopy.repairOrderNumber} and clicked on real job ${job.jobType}/${job.jobCode}`);
            const matchingRo = store.getState().repairOrderSync.repairOrderSync?.data.find((ro) => ro.repairOrderNumber === roCopy.repairOrderNumber && !ro.isAdhoc);
            if (matchingRo) {
              roCopy = { ...matchingRo } as IRepairOrder;
            }
          }

          if (labourClocking?.direction === ClockingDirection.On || labourClocking?.direction === ClockingDirection.Break) {
            // Current labour is either on or on a break
            if (matchingJob) {
              // Trying to clock on existing or paused job
              if (labourClocking?.direction === ClockingDirection.Break) {
                // Resume job
                presentResumeAlert(job);
              } else {
                // Clock off
                showClockOffLabourModal(job, roCopy);
              }
            } else {
              if (labourClocking?.direction === ClockingDirection.On) {
                // Clocking on a new job while on a job
                showClockOffLabourModal(job, roCopy);
              } else {
                // Clocking on a job while another job is paused
                presentClockOnAlert(technician?.code || UNKNOWN_TECH, job, roCopy, selectedRepairOrder?.vehicle?.registration || '');
              }
            }
          } else {
            // Current labour is off
            presentClockOnAlert(technician?.code || UNKNOWN_TECH, job, roCopy, selectedRepairOrder?.vehicle?.registration || '');
          }
        } else {
          const error = `Unexpected error on job selection. Repair order is not defined!`;
          errorToast(error);
          logTransaction(LogType.Error, error);
        }
      } else {
        logTransaction(LogType.Error, `While handling job selection, repair order was undefined in the passed in parameter`);
      }
    } else {
      errorToast('Job is not available');
    }
  };

  const setRepairOrderJobs = async () => {
    if (repairOrder) {
      // Search the existing cache
      if (repairOrder.isAdhoc) {
        // Adhoc RO
        const realJobs = repairOrderSync?.data.find((ro) => ro.repairOrderNumber === repairOrder.repairOrderNumber && !ro.isAdhoc)?.jobs || [];
        const adhocJobs = repairOrderSync?.data.find((ro) => ro.repairOrderNumber === repairOrder.repairOrderNumber && ro.isAdhoc)?.jobs || [];
        const jobs = [...realJobs, ...adhocJobs];
        setJobs(jobs);
      } else {
        // Normal RO
        const realJobs = repairOrderSync?.data.find((ro) => ro.repairOrderNumber === repairOrder.repairOrderNumber && !ro.isAdhoc)?.jobs || [];
        let adhocJobs = [] as IRepairOrderJob[];
        if (labourClocking?.jobData?.isAdhoc) {
          adhocJobs = repairOrderSync?.data.find((ro) => ro.repairOrderNumber === repairOrder.repairOrderNumber && ro.isAdhoc)?.jobs || [];
        }
        const jobs = [...realJobs, ...adhocJobs];
        if (jobs.length) {
          setJobs(jobs);
          if (!isDemo) {
            await dispatch(setRepairOrderHasSyncStoreAsync(false));
            repairOrderSyncTimer?.fire();
            loadJobsManually();
          }
        } else {
          // If there are no jobs, load them manually. It could be that data has no synced yet
          // or clocked on old RO
          await loadJobsManually();
        }
      }
    } else {
      const error = `Unexpected error setting RO jobs. Repair order is not defined!`;
      errorToast(error);
      logTransaction(LogType.Error, error);
    }
  };

  const loadJobsManually = async () => {
    try {
      if (!repairOrder?.isAdhoc) {
        if (repairOrder?.repairOrderNumber) {
          setLoadingJobsManually(true);
          const roSearch = await getRepairOrderByRepairOrderNumber(repairOrder.repairOrderNumber);
          const roWithJobs = mapRepairOrderDetailToRepairOrderWithJobs(roSearch);
          if (roWithJobs?.jobs) {
            if (repairOrder.isAdhoc) {
              // Adhoc RO
              const adhocJobs = repairOrderSync?.data.find((ro) => ro.repairOrderNumber === repairOrder.repairOrderNumber && ro.isAdhoc)?.jobs || [];
              const jobs = [...roWithJobs.jobs, ...adhocJobs];
              setJobs(jobs);
            } else {
              // Normal RO
              let adhocJobs = [] as IRepairOrderJob[];
              if (labourClocking?.jobData?.isAdhoc) {
                adhocJobs = repairOrderSync?.data.find((ro) => ro.repairOrderNumber === repairOrder.repairOrderNumber && ro.isAdhoc)?.jobs || [];
              }
              const jobs = [...roWithJobs.jobs, ...adhocJobs];
              setJobs(jobs);
            }

            await dispatch(updateJobsInRepairOrderSyncStoreAsync({ repairOrderNumber: repairOrder.repairOrderNumber, jobs: roWithJobs.jobs }));
          }
        } else {
          const error = `Unexpected error loading manual jobs. Repair order is not defined!`;
          errorToast(error);
          logTransaction(LogType.Error, error);
        }
      }
    } catch (ex) {
      logTransaction(LogType.Error, `Failed to fetch RO details by RO number for manual load of job card list`);
    } finally {
      setLoadingJobsManually(false);
    }
  };

  const presentClockOnAlert = async (technicianCode: string, jobData: IRepairOrderJob, repairOrder: IRepairOrder, registrationNumber: string) => {
    await presentAlert({
      header: 'Clock On?',
      subHeader: '',
      message: jobData.isAdhoc ? `Are you sure you want to clock on this adhoc job?` : `Are you sure you want to clock on job ${jobData.jobType}/${jobData.jobCode}?`,
      buttons: [
        {
          text: 'No',
          role: 'cancel',
          cssClass: 'secondary-button',
        },
        {
          text: 'Yes',
          cssClass: 'secondary-button',
          handler: async () => {
            await clockOnLabour(technicianCode, jobData, repairOrder, registrationNumber);
            history.push({
              pathname: RouteNames.dashboard,
              search: `tab=${DashboardSelectedTab.ClockingSummary}`,
            });
          },
        },
      ],
      onDidDismiss: () => {
        dispatch(setIsInActionState(false));
      },
    });

    dispatch(setIsInActionState(true));
  };

  const presentResumeAlert = async (jobData: IRepairOrderJob) => {
    await presentAlert({
      header: 'Resume?',
      subHeader: '',
      message: jobData.isAdhoc ? `Are you sure you want to resume this adhoc job?` : `Are you sure you want to resume job ${jobData.jobType}/${jobData.jobCode}?`,
      buttons: [
        {
          text: 'No',
          role: 'cancel',
          cssClass: 'secondary-button',
        },
        {
          text: 'Yes',
          cssClass: 'secondary-button',
          handler: async () => {
            await resumeLabour();
          },
        },
      ],
      onDidDismiss: () => {
        dispatch(setIsInActionState(false));
      },
    });

    dispatch(setIsInActionState(true));
  };

  const clockOnLabour = async (technicianCode: string, jobData: IRepairOrderJob, repairOrder: IRepairOrder, registrationNumber: string) => {
    const startDateTime = new Date();
    const { lastPosition } = store.getState().location;

    try {
      logTransaction(LogType.Info, `Clock on labour requested - jobData: ${jobData.repairOrderNumber}/${jobData.jobCode}`);
      const resultAction = await dispatch(
        clockOnLabourClockingAsync({
          techCode: technicianCode,
          startDateTime: getStateDateAsString(startDateTime),
          jobData: jobData,
          repairOrder: repairOrder,
          position: lastPosition,
          registrationNumber: registrationNumber,
        }),
      );
      unwrapResult(resultAction);
      requestQueueSyncTimer?.fire();
    } catch (ex) {
      const err = (ex as IThunkError).message;
      logTransaction(LogType.Error, `Failed to clock on labour - jobData: ${jobData.repairOrderNumber}/${jobData.jobCode}. ${err}`);
      errorToast(err);
    }
  };

  const resumeLabour = async () => {
    try {
      const startDateTime = new Date();
      const { lastPosition } = store.getState().location;

      logTransaction(LogType.Info, `Resume labour requested`);
      const resultAction = await dispatch(
        resumeLabourClockingAsync({
          startDateTime: getStateDateAsString(startDateTime),
          position: lastPosition,
        }),
      );
      unwrapResult(resultAction);
      requestQueueSyncTimer?.fire();
    } catch (ex) {
      const err = (ex as IThunkError).message;
      logTransaction(LogType.Error, `Failed to resume labour. ${err}`);
      errorToast(err);
    }
  };

  return (
    <>
      <IonGridWrapper>
        <IonRow>
          {jobs.map((job, index) => {
            return (
              <IonCol key={keyId + getJobId(job) + index} size-xs="12" size-sm="12" size-md="6" size-lg="6" size-xl="4">
                <JobCard job={job} onClick={() => handleClickJob(job)} />
              </IonCol>
            );
          })}
          {(repairOrderSyncLoading || loadingJobsManually) && (
            <IonCol size-xs="12" size-sm="12" size-md="6" size-lg="6" size-xl="4">
              <JobCardSkeleton />
            </IonCol>
          )}
        </IonRow>
      </IonGridWrapper>
    </>
  );
};

export default forwardRef(JobCardList);
